// src/pages/ForgotPassword.jsx
import React, { useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

function classNames(...arr) {
  return arr.filter(Boolean).join(" ");
}

function Icon({ name, className = "h-5 w-5" }) {
  switch (name) {
    case "mail":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6Z" stroke="currentColor" strokeWidth="2.2" />
          <path d="m5.5 7.5 6.2 5a1 1 0 0 0 1.3 0l6.2-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );

    case "lock":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path d="M7 10V8a5 5 0 0 1 10 0v2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          <path d="M6 10h12a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="2.2" />
          <path d="M12 14v3" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      );

    case "eye":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path d="M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7Z" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 15.2A3.2 3.2 0 1 0 12 8.8a3.2 3.2 0 0 0 0 6.4Z" stroke="currentColor" strokeWidth="2.2" />
        </svg>
      );

    case "eyeOff":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path d="M10.6 5.2A10.8 10.8 0 0 1 12 5c6 0 9.5 7 9.5 7a18 18 0 0 1-2.2 3.3" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          <path d="M6.3 6.3C3.7 8.2 2.5 12 2.5 12s3.5 7 9.5 7c1.7 0 3.2-.3 4.5-.9" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          <path d="M9.6 9.6A3.2 3.2 0 0 0 12 15.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      );

    case "shield":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path d="M12 3 20 6v6c0 5-3.4 8.3-8 9-4.6-.7-8-4-8-9V6l8-3Z" stroke="currentColor" strokeWidth="2.1" strokeLinejoin="round" />
          <path d="m8.8 12 2 2 4.6-5" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );

    case "heart":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path d="M20.8 5.6a5.2 5.2 0 0 0-7.4 0L12 7l-1.4-1.4a5.2 5.2 0 0 0-7.4 7.4L12 21.7 20.8 13a5.2 5.2 0 0 0 0-7.4Z" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );

    case "clock":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.1" />
          <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );

    case "check":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path d="m5 13 4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );

    case "arrowRight":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );

    default:
      return null;
  }
}

function Field({ label, children, right, error }) {
  return (
    <div>
      <div className="flex items-end justify-between gap-3">
        <label className="text-sm font-semibold text-slate-800">{label}</label>
        {right}
      </div>
      <div className="mt-2">{children}</div>
      {error ? <div className="mt-2 text-xs font-semibold text-rose-600">{error}</div> : null}
    </div>
  );
}

function FloatingCard({ className = "", children }) {
  return (
    <div className={classNames("rounded-[28px] border border-slate-200/80 bg-white/90 p-4 text-slate-900 shadow-[0_24px_80px_rgba(15,23,42,0.12)] backdrop-blur-xl", className)}>
      {children}
    </div>
  );
}

function HeroFeature({ icon, title, text }) {
  return (
    <div className="flex gap-3">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-blue-100 bg-blue-50 text-blue-700">
        <Icon name={icon} className="h-5 w-5" />
      </div>
      <div>
        <div className="text-sm font-extrabold text-slate-950">{title}</div>
        <div className="mt-1 text-xs leading-5 text-slate-500">{text}</div>
      </div>
    </div>
  );
}

function LeftVisual() {
  return (
    <section className="relative hidden h-screen overflow-hidden bg-white lg:col-span-7 lg:block">
      <div className="absolute -left-28 bottom-[-140px] h-[420px] w-[420px] rounded-full bg-blue-200/45 blur-3xl" />
      <div className="absolute right-[-120px] top-[-120px] h-[360px] w-[360px] rounded-full bg-cyan-200/45 blur-3xl" />
      <div className="absolute left-[58%] top-[18%] h-[240px] w-[240px] rounded-full bg-violet-200/35 blur-3xl" />

      <div className="absolute inset-0 opacity-[0.42]">
        <svg className="h-full w-full" viewBox="0 0 900 900" fill="none">
          <defs>
            <pattern id="forgot-grid" width="44" height="44" patternUnits="userSpaceOnUse">
              <path d="M44 0H0V44" stroke="#2563EB" strokeWidth="1" opacity="0.16" />
            </pattern>
          </defs>
          <rect width="900" height="900" fill="url(#forgot-grid)" />
        </svg>
      </div>

      <svg className="absolute left-[6%] top-[24%] h-[280px] w-[88%] opacity-35" viewBox="0 0 900 260" fill="none">
        <path d="M0 145 C110 80 190 80 280 140 C350 188 410 185 455 110 L480 210 L515 65 L545 148 C590 145 620 145 675 110 C760 55 835 50 900 80" stroke="#2563EB" strokeWidth="3" strokeLinecap="round" />
        <path d="M0 176 C120 130 215 125 310 166 C405 205 490 205 585 158 C710 96 805 96 900 122" stroke="#06B6D4" strokeWidth="1.8" strokeLinecap="round" opacity="0.55" />
      </svg>

      <div className="relative z-10 flex min-h-screen flex-col px-10 py-9 xl:px-14">
        <div className="flex items-center gap-4">
          <img src="/images/logo.png" alt="ThePulseCare" className="h-14 w-auto object-contain" />
          <div>
            <div className="text-2xl font-extrabold tracking-tight text-slate-950">ThePulseCare</div>
            <div className="text-sm font-bold text-slate-500">Patient Portal</div>
          </div>
        </div>

        <div className="mt-20 max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            Safe password recovery
          </div>

          <h1 className="mt-6 text-6xl font-black leading-[0.98] tracking-tight text-slate-950 xl:text-7xl">
            Reset access
            <br />
            securely and
            <br />
            <span className="bg-gradient-to-r from-blue-700 via-sky-500 to-cyan-500 bg-clip-text text-transparent">quickly</span>
          </h1>

          <p className="mt-7 max-w-xl text-lg leading-8 text-slate-600">
            Use your registered email, verify the OTP, and create a fresh password for your patient account.
          </p>
        </div>

        <FloatingCard className="absolute right-[10%] top-[24%] w-[285px]">
          <div className="text-xs font-bold text-slate-500">Reset protection</div>
          <div className="mt-3 flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
              <Icon name="shield" className="h-6 w-6" />
            </div>
            <div>
              <div className="text-sm font-black text-slate-950">6-digit OTP</div>
              <div className="text-xs text-slate-500">Sent only to registered email</div>
            </div>
          </div>
        </FloatingCard>

        <FloatingCard className="absolute right-[5%] top-[47%] w-[310px]">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-50 text-violet-600">
              <Icon name="clock" className="h-6 w-6" />
            </div>
            <div>
              <div className="text-sm font-black text-slate-950">Short-lived code</div>
              <div className="text-xs text-slate-500">Expired and used OTPs cannot reset passwords.</div>
            </div>
          </div>
        </FloatingCard>

        <div className="mt-auto grid grid-cols-3 gap-5 border-t border-slate-200/80 pt-8">
          <HeroFeature icon="mail" title="Email OTP" text="The reset code is sent to the account email." />
          <HeroFeature icon="shield" title="Hashed code" text="The backend stores a hash, not the raw OTP." />
          <HeroFeature icon="lock" title="Fresh password" text="Create a stronger password after OTP verification." />
        </div>
      </div>
    </section>
  );
}

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  "http://ec2-34-228-161-76.compute-1.amazonaws.com:8001";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function passwordScore(password) {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  return Math.min(score, 4);
}

function passwordLabel(score) {
  if (score <= 1) return "Weak";
  if (score === 2) return "Good";
  if (score === 3) return "Strong";
  return "Excellent";
}

function getApiError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg || item.message || String(item)).join(", ");
  if (typeof detail === "string") return detail;
  return error?.response?.data?.message || error?.message || fallback;
}

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get("email") || "";

  const [step, setStep] = useState(0);
  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [expiresInMinutes, setExpiresInMinutes] = useState(10);

  const otpRefs = useRef([]);
  const score = useMemo(() => passwordScore(password), [password]);
  const otpValue = otp.join("");

  const input =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-primary focus:ring-4 focus:ring-primary/10 disabled:bg-slate-50 disabled:text-slate-500";
  const inputWithIcon = classNames(input, "pl-12");

  async function requestOtp(event) {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!isValidEmail(email.trim())) {
      setError("Please enter a valid registered email address.");
      return;
    }

    try {
      setLoading(true);
      const response = await api.post("/api/v1/patients/forgot-password", {
        email: email.trim(),
      });
      setExpiresInMinutes(response.data?.expires_in_minutes || 10);
      setMessage(response.data?.message || "We sent a 6-digit OTP to your email.");
      setStep(1);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      setError(getApiError(err, "Failed to send OTP. Please try again."));
    } finally {
      setLoading(false);
    }
  }

  function updateOtp(index, value) {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    setError("");

    if (digit && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  }

  function handleOtpKeyDown(index, event) {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  }

  function handleOtpPaste(event) {
    event.preventDefault();
    const digits = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6).split("");
    if (!digits.length) return;

    const next = ["", "", "", "", "", ""];
    digits.forEach((digit, index) => {
      next[index] = digit;
    });
    setOtp(next);
    otpRefs.current[Math.min(digits.length, 5)]?.focus();
  }

  function continueToPassword(event) {
    event.preventDefault();
    setError("");

    if (otpValue.length !== 6) {
      setError("Please enter the 6-digit OTP sent to your email.");
      return;
    }

    setStep(2);
  }

  async function resetPassword(event) {
    event.preventDefault();
    setError("");
    setMessage("");

    if (otpValue.length !== 6) {
      setError("Please enter the complete 6-digit OTP.");
      setStep(1);
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);
      const response = await api.post("/api/v1/patients/reset-password", {
        email: email.trim(),
        otp: otpValue,
        new_password: password,
        confirm_password: confirmPassword,
      });
      setMessage(response.data?.message || "Password changed successfully.");
      setStep(3);
    } catch (err) {
      setError(getApiError(err, "Failed to reset password. Please try again."));
      if (err?.response?.status === 400 && /otp/i.test(getApiError(err, ""))) {
        setStep(1);
      }
    } finally {
      setLoading(false);
    }
  }

  function resetToEmail() {
    setStep(0);
    setOtp(["", "", "", "", "", ""]);
    setPassword("");
    setConfirmPassword("");
    setError("");
    setMessage("");
  }

  return (
    <main className="h-screen overflow-hidden bg-slate-50">
      <div className="grid h-screen grid-cols-1 lg:grid-cols-12">
        <LeftVisual />

        <section className="relative h-screen overflow-y-auto px-5 py-8 sm:px-8 lg:col-span-5 lg:px-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,165,233,0.13),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(124,58,237,0.10),transparent_30%)]" />

          <div className="relative mx-auto flex min-h-full w-full max-w-[520px] flex-col justify-center py-8">
            <div className="mb-8 flex items-center justify-center gap-3 lg:hidden">
              <img src="/images/logo.png" alt="ThePulseCare" className="h-12 w-auto object-contain" />
              <div>
                <div className="text-xl font-extrabold text-slate-950">ThePulseCare</div>
                <div className="text-xs font-bold text-slate-500">Patient Portal</div>
              </div>
            </div>

            <div className="overflow-hidden rounded-[32px] border border-white/80 bg-white/90 shadow-[0_24px_80px_rgba(15,23,42,0.13)] backdrop-blur-xl">
              <div className="p-7 sm:p-9">
                <div className="inline-flex items-center gap-2 rounded-full border border-sky-100 bg-sky-50 px-3 py-1 text-xs font-bold text-sky-700">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  Secure password reset
                </div>

                <h2 className="mt-5 text-4xl font-black tracking-tight text-slate-950">
                  {step === 3 ? "Password updated" : "Forgot password?"}
                </h2>

                <p className="mt-3 max-w-sm text-base leading-7 text-slate-500">
                  {step === 0
                    ? "Enter your registered patient email. We will send a 6-digit OTP to reset your password."
                    : step === 1
                    ? `Enter the OTP sent to ${email.trim()}. The code expires in ${expiresInMinutes} minutes.`
                    : step === 2
                    ? "Create a new password that is easy for you to remember and hard for others to guess."
                    : "Your password has been changed. You can sign in using the new password."}
                </p>

                <div className="mt-6 grid grid-cols-3 gap-2">
                  {["Email", "OTP", "Password"].map((label, index) => (
                    <div key={label}>
                      <div className={classNames("h-2 rounded-full", step >= index ? "bg-primary" : "bg-slate-200")} />
                      <div className={classNames("mt-2 text-xs font-black", step >= index ? "text-primary" : "text-slate-400")}>{label}</div>
                    </div>
                  ))}
                </div>

                {error ? (
                  <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
                    {error}
                  </div>
                ) : null}

                {message ? (
                  <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
                    {message}
                  </div>
                ) : null}

                {step === 0 ? (
                  <form onSubmit={requestOtp} className="mt-8 space-y-5">
                    <Field label="Registered email address">
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                          <Icon name="mail" className="h-5 w-5" />
                        </span>
                        <input
                          value={email}
                          onChange={(event) => {
                            setEmail(event.target.value);
                            setError("");
                          }}
                          type="email"
                          className={inputWithIcon}
                          placeholder="Enter your patient email address"
                          required
                          disabled={loading}
                        />
                      </div>
                    </Field>

                    <button
                      type="submit"
                      className="group flex w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-500 px-6 py-4 text-sm font-extrabold text-white shadow-[0_16px_40px_rgba(37,99,235,0.28)] transition hover:-translate-y-0.5 hover:shadow-[0_20px_50px_rgba(37,99,235,0.34)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
                      disabled={loading}
                    >
                      {loading ? "Sending OTP..." : "Send OTP"}
                      {!loading ? <Icon name="arrowRight" className="h-5 w-5 transition group-hover:translate-x-1" /> : null}
                    </button>
                  </form>
                ) : null}

                {step === 1 ? (
                  <form onSubmit={continueToPassword} className="mt-8 space-y-5">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                      <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Reset email</div>
                      <div className="mt-1 flex items-center justify-between gap-3">
                        <div className="truncate text-sm font-black text-slate-800">{email.trim()}</div>
                        <button type="button" onClick={resetToEmail} className="text-xs font-black text-primary hover:underline" disabled={loading}>
                          Change
                        </button>
                      </div>
                    </div>

                    <Field
                      label="Enter OTP"
                      right={
                        <button type="button" onClick={requestOtp} className="text-sm font-bold text-primary hover:underline disabled:opacity-50" disabled={loading}>
                          Resend OTP
                        </button>
                      }
                    >
                      <div className="grid grid-cols-6 gap-2" onPaste={handleOtpPaste}>
                        {otp.map((digit, index) => (
                          <input
                            key={index}
                            ref={(node) => {
                              otpRefs.current[index] = node;
                            }}
                            value={digit}
                            onChange={(event) => updateOtp(index, event.target.value)}
                            onKeyDown={(event) => handleOtpKeyDown(index, event)}
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength="1"
                            className="h-14 rounded-2xl border border-slate-200 bg-white text-center text-xl font-black text-slate-950 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10 disabled:bg-slate-50"
                            disabled={loading}
                          />
                        ))}
                      </div>
                    </Field>

                    <button
                      type="submit"
                      className="group flex w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-500 px-6 py-4 text-sm font-extrabold text-white shadow-[0_16px_40px_rgba(37,99,235,0.28)] transition hover:-translate-y-0.5 hover:shadow-[0_20px_50px_rgba(37,99,235,0.34)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
                      disabled={loading}
                    >
                      Continue
                      <Icon name="arrowRight" className="h-5 w-5 transition group-hover:translate-x-1" />
                    </button>
                  </form>
                ) : null}

                {step === 2 ? (
                  <form onSubmit={resetPassword} className="mt-8 space-y-5">
                    <Field label="New password">
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                          <Icon name="lock" className="h-5 w-5" />
                        </span>
                        <input
                          value={password}
                          onChange={(event) => {
                            setPassword(event.target.value);
                            setError("");
                          }}
                          type={showPassword ? "text" : "password"}
                          className={classNames(inputWithIcon, "pr-14")}
                          placeholder="Create a new password"
                          minLength="8"
                          required
                          disabled={loading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((value) => !value)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                          disabled={loading}
                          title={showPassword ? "Hide password" : "Show password"}
                        >
                          <Icon name={showPassword ? "eyeOff" : "eye"} className="h-5 w-5" />
                        </button>
                      </div>
                    </Field>

                    {password ? (
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-xs font-black uppercase tracking-wide text-slate-400">Password strength</div>
                          <div className="text-xs font-black text-slate-700">{passwordLabel(score)}</div>
                        </div>
                        <div className="mt-3 grid grid-cols-4 gap-2">
                          {[1, 2, 3, 4].map((item) => (
                            <div key={item} className={classNames("h-2 rounded-full", item <= score ? "bg-primary" : "bg-slate-200")} />
                          ))}
                        </div>
                      </div>
                    ) : null}

                    <Field label="Confirm password">
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                          <Icon name="lock" className="h-5 w-5" />
                        </span>
                        <input
                          value={confirmPassword}
                          onChange={(event) => {
                            setConfirmPassword(event.target.value);
                            setError("");
                          }}
                          type={showConfirmPassword ? "text" : "password"}
                          className={classNames(inputWithIcon, "pr-14")}
                          placeholder="Re-enter your new password"
                          minLength="8"
                          required
                          disabled={loading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((value) => !value)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                          disabled={loading}
                          title={showConfirmPassword ? "Hide password" : "Show password"}
                        >
                          <Icon name={showConfirmPassword ? "eyeOff" : "eye"} className="h-5 w-5" />
                        </button>
                      </div>
                    </Field>

                    <button
                      type="submit"
                      className="group flex w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-500 px-6 py-4 text-sm font-extrabold text-white shadow-[0_16px_40px_rgba(37,99,235,0.28)] transition hover:-translate-y-0.5 hover:shadow-[0_20px_50px_rgba(37,99,235,0.34)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
                      disabled={loading}
                    >
                      {loading ? "Updating password..." : "Update password"}
                    </button>
                  </form>
                ) : null}

                {step === 3 ? (
                  <div className="mt-8 space-y-5">
                    <div className="flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 text-emerald-700">
                      <Icon name="check" className="h-8 w-8" />
                    </div>

                    <button
                      type="button"
                      onClick={() => navigate("/signin", { replace: true })}
                      className="group flex w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-500 px-6 py-4 text-sm font-extrabold text-white shadow-[0_16px_40px_rgba(37,99,235,0.28)] transition hover:-translate-y-0.5 hover:shadow-[0_20px_50px_rgba(37,99,235,0.34)]"
                    >
                      Go to sign in
                      <Icon name="arrowRight" className="h-5 w-5 transition group-hover:translate-x-1" />
                    </button>
                  </div>
                ) : null}
              </div>

              <div className="border-t border-slate-100 bg-slate-50/80 px-7 py-5 sm:px-9">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-slate-500 shadow-sm">
                    <Icon name="shield" className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-800">Need your old password?</div>
                    <div className="mt-0.5 text-xs leading-5 text-slate-500">
                      No. The OTP verifies your email, then you can create a fresh password.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 text-center text-sm text-slate-500">
              Remembered your password?{" "}
              <Link to="/signin" className="font-extrabold text-primary hover:underline">
                Sign in
              </Link>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
